import { createTheme, alpha, PaletteMode, Shadows } from "@mui/material/styles";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

export const fpPrimary = {
  50: "hsl(200.7, 100%, 95%)",
  100: "hsl(200.7, 100%, 92%)",
  200: "hsl(200.7, 100%, 80%)",
  300: "hsl(200.7, 100%, 65%)",
  400: "hsl(200.7, 100%, 48%)",
  500: "hsl(200.7, 100%, 42%)",
  600: "hsl(200.7, 100%, 55%)",
  700: "hsl(200.7, 100%, 35%)",
  800: "hsl(200.7, 100%, 16%)",
  900: "hsl(200.7, 100%, 11.4%)",
};

export const fpSecondary = {
  50: "hsl(111.5, 67.5%, 95%)",
  100: "hsl(111.5, 67.5%, 92%)",
  200: "hsl(111.5, 67.5%, 80%)",
  300: "hsl(111.5, 67.5%, 65%)",
  400: "hsl(111.5, 67.5%, 50.6%)",
  500: "hsl(111.5, 67.5%, 42%)",
  600: "hsl(111.5, 67.5%, 55%)",
  700: "hsl(111.5, 67.5%, 35%)",
  800: "hsl(111.5, 67.5%, 16%)",
  900: "hsl(111.5, 67.5%, 11.4%)",
};

export const gray = {
  50: "hsl(220, 35%, 97%)",
  100: "hsl(220, 30%, 94%)",
  200: "hsl(220, 20%, 88%)",
  300: "hsl(220, 20%, 80%)",
  400: "hsl(220, 20%, 65%)",
  500: "hsl(220, 20%, 42%)",
  600: "hsl(220, 20%, 35%)",
  700: "hsl(220, 20%, 25%)",
  800: "hsl(220, 30%, 6%)",
  900: "hsl(220, 35%, 3%)",
};

export const green = {
  50: "hsl(120, 80%, 98%)",
  100: "hsl(120, 75%, 94%)",
  200: "hsl(120, 75%, 87%)",
  300: "hsl(120, 61%, 77%)",
  400: "hsl(120, 44%, 53%)",
  500: "hsl(120, 59%, 30%)",
  600: "hsl(120, 70%, 25%)",
  700: "hsl(120, 75%, 16%)",
  800: "hsl(120, 84%, 10%)",
  900: "hsl(120, 87%, 6%)",
};

export const orange = {
  50: "hsl(45, 100%, 97%)",
  100: "hsl(45, 92%, 90%)",
  200: "hsl(45, 94%, 80%)",
  300: "hsl(45, 90%, 65%)",
  400: "hsl(45, 90%, 40%)",
  500: "hsl(45, 90%, 35%)",
  600: "hsl(45, 91%, 25%)",
  700: "hsl(45, 94%, 20%)",
  800: "hsl(45, 95%, 16%)",
  900: "hsl(45, 93%, 12%)",
};

export const red = {
  50: "hsl(0, 100%, 97%)",
  100: "hsl(0, 92%, 90%)",
  200: "hsl(0, 94%, 80%)",
  300: "hsl(0, 90%, 65%)",
  400: "hsl(0, 90%, 40%)",
  500: "hsl(0, 90%, 30%)",
  600: "hsl(0, 91%, 25%)",
  700: "hsl(0, 94%, 18%)",
  800: "hsl(0, 95%, 12%)",
  900: "hsl(0, 93%, 6%)",
};

export const blue = {
  50: "hsl(197, 36%, 98%)",
  100: "hsl(197, 36%, 94%)",
  200: "hsl(197, 36%, 87%)",
  300: "hsl(197, 36%, 77%)",
  400: "hsl(197, 36%, 53%)",
  500: "hsl(197, 36%, 30%)",
  600: "hsl(197, 36%, 25%)",
  700: "hsl(197, 36%, 16%)",
  800: "hsl(197, 36%, 10%)",
  900: "hsl(197, 36%, 6%)",
};

export const yellow = {
  50: "hsl(52, 100%, 98%)",
  100: "hsl(52, 100%, 94%)",
  200: "hsl(52, 100%, 87%)",
  300: "hsl(52, 100%, 77%)",
  400: "hsl(52, 100%, 53%)",
  500: "hsl(52, 100%, 30%)",
  600: "hsl(52, 100%, 25%)",
  700: "hsl(52, 100%, 16%)",
  800: "hsl(52, 100%, 10%)",
  900: "hsl(52, 100%, 6%)",
};

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === "dark"
      ? "hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px"
      : "hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px";

  return {
    colors: {
      fpdarkblue: "#00263A",
      fpgreen: "#44D62C",
      fpgrey: "#A7A8A9",
      fplightblue: "#4E87A0",
      fpyellow: "#FEDD00",
      red: "#f84b4b",
    },
    fonts: {
      primary: "'GreycliffCF-Regular', 'Kumbh Sans', sans-serif", // primary font
      secondary: "'Kumbh Sans', sans-serif", // secondary font
    },
    fontSizes: {
      small: "15px",
      smallmedium: "20px",
      medium: "25px",
      mediumlarge: "33px",
      large: "40px",
      xlarge: "70px",
    },
    fontWeight: {
      regular: "'GreycliffCF-Regular', 'Kumbh Sans', sans-serif",
      bold: "'GreycliffCF-Bold', 'Kumbh Sans', sans-serif",
    },
    palette: {
      mode,
      primary: {
        light: green[200],
        main: fpPrimary[900],
        dark: green[700],
        contrastText: green[50],
        ...(mode === "dark" && {
          contrastText: green[50],
          light: green[300],
          main: green[400],
          dark: green[700],
        }),
      },
      info: {
        light: blue[100],
        main: blue[300],
        dark: blue[600],
        contrastText: gray[50],
        ...(mode === "dark" && {
          contrastText: blue[300],
          light: blue[500],
          main: blue[700],
          dark: blue[900],
        }),
      },
      warning: {
        light: yellow[300],
        main: yellow[400],
        dark: yellow[800],
        ...(mode === "dark" && {
          light: yellow[400],
          main: yellow[500],
          dark: yellow[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === "dark" && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === "dark" && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === "dark" ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
      background: {
        default: "hsl(0, 0%, 99%)",
        paper: "hsl(220, 35%, 97%)",
        ...(mode === "dark" && {
          default: gray[900],
          paper: "hsl(220, 30%, 7%)",
        }),
      },
      text: {
        primary: fpPrimary[900],
        secondary: gray[600],
        warning: orange[400],
        ...(mode === "dark" && {
          primary: "hsl(0, 0%, 100%)",
          secondary: gray[400],
        }),
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        ...(mode === "dark" && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3),
        }),
      },
    },
    typography: {
      fontFamily: [
        "GreycliffCF-Regular",
        "GreycliffCF-Bold",
        "Kumbh Sans",
        "sans-serif",
      ].join(","),
      h1: {
        fontSize: "70px",
        fontWeight: 700,
      },
      h2: {
        fontSize: "33px",
        fontWeight: 700,
      },
      h3: {
        fontSize: "25px",
        fontWeight: 700,
      },
      h4: {
        fontSize: "20px",
        fontWeight: 700,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 700,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(16),
        fontWeight: 700,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 700,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 700,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 700,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: customShadows,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "#FFFFFF",
            borderWidth: 0,
            borderBottomWidth: 1,
            borderStyle: "solid",
            borderColor: fpSecondary[400],
            boxShadow: "none",
            // TODO: Temporary fix to issue reported in https://github.com/mui/material-ui/issues/43244
            left: 0,
            zIndex: defaultTheme.zIndex.drawer + 1,
          },
        },
      },
      // MuiTypography: {
      //   styleOverrides: {
      //     root: {
      //       color: '#00263A',
      //       fontFamily: "GreycliffCF-Regular, Kumbh Sans, sans-serif",
      //       "&.MuiPickersToolbarText-root.Mui-selected": {
      //         color: "#FFF !important",
      //       },
      //     }
      //   }
      // },
      // MuiButton: {
      //   styleOverrides: {
      //     root: {
      //       "&:hover": {
      //         opacity: .7,
      //       },
      //       fontWeight: 600,
      //       fontFamily: "GreycliffCF-Regular, Kumbh Sans, sans-serif"
      //     },
      //   }
      // },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#FFFFFF",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: "#00263A",
          },
        },
      },
    },
  };
};

export default createTheme(getDesignTokens("light"));

// // theme/index.ts
// import { createTheme, alpha } from "@mui/material/styles";

// // FreightPlus base colors
// const palette = {
//   primary: { main: "#00263A" },   // dark blue
//   secondary: { main: "#44D62C" }, // green
//   error: { main: "#f84b4b" },
//   warning: { main: "#FEDD00" },
//   info: { main: "#4E87A0" },
//   grey: { 500: "#A7A8A9" },
// };

// // Custom brand tokens for styled-components (optional)
// export const brand = {
//   colors: {
//     fpdarkblue: palette.primary.main,
//     fpgreen: palette.secondary.main,
//     fpgrey: palette.grey[500],
//     fplightblue: palette.info.main,
//     fpyellow: palette.warning.main,
//     red: palette.error.main,
//   },
//   fontSizes: {
//     small: "15px",
//     medium: "20px",
//     large: "25px",
//   },
// };

// const theme = createTheme({
//   palette,
//   typography: {
//     fontFamily: "'Kumbh Sans', sans-serif",
//     button: {
//       textTransform: "none",
//       fontWeight: 700,
//     },
//   },
//   shape: {
//     borderRadius: 5, // match your button style
//   },
//   components: {
//     MuiCssBaseline: {
//       styleOverrides: {
//         body: { backgroundColor: "#fafafa" },
//       },
//     },
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: 5,
//           fontWeight: 700,
//         },
//       },
//     },
//   },
// });

// export default theme;
